import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { getIdToken } from '../Firebase';

const API = `${process.env.REACT_APP_API_URL}/api/admin/europe-draws`;

const emptySlots = (count) => Array.from({ length: count }, () => null);

const competitionLabel = (competition) => {
  if (!competition) return '';
  return competition.group
    ? `${competition.name} (${competition.group})`
    : competition.name;
};

const authConfig = async () => {
  const token = await getIdToken(false);
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const byName = (a, b) => a.name.localeCompare(b.name, 'pl');

const EuropeDrawsManager = () => {
  const [competitionId, setCompetitionId] = useState(836);
  const [data, setData] = useState({
    competitions: [],
    teams: [],
    matches: [],
    teamCount: 0,
    matchCount: 0,
    expectedMatchCount: 0,
    completeTeamCount: 0,
    matchesPerSide: 4,
  });
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [homeSlots, setHomeSlots] = useState(emptySlots(4));
  const [awaySlots, setAwaySlots] = useState(emptySlots(4));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const selectedTeam = useMemo(
    () => data.teams.find((team) => team.teamId === selectedTeamId) || null,
    [data.teams, selectedTeamId]
  );
  const matchesPerSide = data.matchesPerSide || (competitionId === 838 ? 3 : 4);

  const teamsById = useMemo(() => {
    const map = new Map();
    data.teams.forEach((team) => map.set(team.teamId, team));
    return map;
  }, [data.teams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}?competitionId=${competitionId}`, await authConfig());
      const next = response.data || {};
      setData({
        competitions: next.competitions || [],
        teams: next.teams || [],
        matches: next.matches || [],
        teamCount: next.teamCount || 0,
        matchCount: next.matchCount || 0,
        expectedMatchCount: next.expectedMatchCount || 0,
        completeTeamCount: next.completeTeamCount || 0,
        matchesPerSide: next.matchesPerSide || (competitionId === 838 ? 3 : 4),
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (data.teams.length === 0) {
      setSelectedTeamId('');
      return;
    }
    if (!data.teams.some((team) => team.teamId === selectedTeamId)) {
      const firstIncomplete = data.teams.find((team) => !team.complete);
      setSelectedTeamId((firstIncomplete || data.teams[0]).teamId);
    }
  }, [data.teams, selectedTeamId]);

  useEffect(() => {
    if (!selectedTeamId) {
      setHomeSlots(emptySlots(matchesPerSide));
      setAwaySlots(emptySlots(matchesPerSide));
      return;
    }

    const homeTeams = data.matches
      .filter((match) => match.homeTeamId === selectedTeamId)
      .map((match) => teamsById.get(match.awayTeamId))
      .filter(Boolean)
      .sort(byName)
      .slice(0, matchesPerSide);

    const awayTeams = data.matches
      .filter((match) => match.awayTeamId === selectedTeamId)
      .map((match) => teamsById.get(match.homeTeamId))
      .filter(Boolean)
      .sort(byName)
      .slice(0, matchesPerSide);

    setHomeSlots([...homeTeams, ...emptySlots(matchesPerSide)].slice(0, matchesPerSide));
    setAwaySlots([...awayTeams, ...emptySlots(matchesPerSide)].slice(0, matchesPerSide));
  }, [data.matches, matchesPerSide, selectedTeamId, teamsById]);

  const selectedOpponentIds = useMemo(() => {
    const ids = new Set();
    [...homeSlots, ...awaySlots].forEach((team) => {
      if (team) ids.add(team.teamId);
    });
    return ids;
  }, [homeSlots, awaySlots]);

  const optionsForSlot = (currentValue) => data.teams.filter((team) => (
    team.teamId !== selectedTeamId
      && (!selectedOpponentIds.has(team.teamId) || currentValue?.teamId === team.teamId)
  ));

  const setSlot = (side, index, value) => {
    const setter = side === 'home' ? setHomeSlots : setAwaySlots;
    setter((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const findMatch = (side, opponent) => {
    if (!selectedTeamId || !opponent) return null;
    return data.matches.find((match) => (
      side === 'home'
        ? match.homeTeamId === selectedTeamId && match.awayTeamId === opponent.teamId
        : match.homeTeamId === opponent.teamId && match.awayTeamId === selectedTeamId
    ));
  };

  const filledHome = homeSlots.filter(Boolean).length;
  const filledAway = awaySlots.filter(Boolean).length;
  const canSave = selectedTeam && filledHome === matchesPerSide && filledAway === matchesPerSide && !saving;
  const progress = data.expectedMatchCount
    ? Math.min(100, Math.round((data.matchCount / data.expectedMatchCount) * 100))
    : 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(API + '/matches', {
        competitionId,
        teamId: selectedTeamId,
        homeOpponentIds: homeSlots.map((team) => team.teamId),
        awayOpponentIds: awaySlots.map((team) => team.teamId),
      }, await authConfig());

      const next = response.data || {};
      setData({
        competitions: next.competitions || data.competitions,
        teams: next.teams || data.teams,
        matches: next.matches || data.matches,
        teamCount: next.teamCount ?? data.teamCount,
        matchCount: next.matchCount ?? data.matchCount,
        expectedMatchCount: next.expectedMatchCount ?? data.expectedMatchCount,
        completeTeamCount: next.completeTeamCount ?? data.completeTeamCount,
        matchesPerSide: next.matchesPerSide ?? data.matchesPerSide,
      });
      setMessage(next.message || 'Mecze zapisane.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const goToNextIncomplete = () => {
    const next = data.teams.find((team) => !team.complete && team.teamId !== selectedTeamId);
    if (next) setSelectedTeamId(next.teamId);
  };

  const renderSlot = (side, index, value) => {
    const existingMatch = findMatch(side, value);
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} key={`${side}-${index}`}>
        <Autocomplete
          options={optionsForSlot(value)}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, selected) => option.teamId === selected.teamId}
          value={value}
          onChange={(event, nextValue) => setSlot(side, index, nextValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${side === 'home' ? 'U siebie' : 'Na wyjeździe'} ${index + 1}`}
              size="small"
            />
          )}
          sx={{ flex: 1 }}
        />
        <Box sx={{ width: { xs: '100%', sm: 118 }, minHeight: 32, display: 'flex', alignItems: 'center' }}>
          {existingMatch ? (
            <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`#${existingMatch.matchId}`} />
          ) : value ? (
            <Chip size="small" variant="outlined" label="nowy" />
          ) : null}
        </Box>
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Ładowanie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Losowania Europejskich Pucharów
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ręczne dodawanie zestawu spotkań fazy ligowej przed publikacją terminarza.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || saving}>
            Odśwież
          </Button>
          <Button variant="outlined" onClick={goToNextIncomplete} disabled={!data.teams.some((team) => !team.complete)}>
            Następna niepełna
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: '#1e1e1e' }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rozgrywki</InputLabel>
                  <Select
                    label="Rozgrywki"
                    value={competitionId}
                    onChange={(event) => {
                      setCompetitionId(Number(event.target.value));
                      setMessage('');
                      setError('');
                    }}
                  >
                    {data.competitions.map((competition) => (
                      <MenuItem key={competition.competitionId} value={competition.competitionId}>
                        {competitionLabel(competition)} [{competition.competitionId}]
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={data.teams}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, selected) => option.teamId === selected.teamId}
                  value={selectedTeam}
                  onChange={(event, nextValue) => setSelectedTeamId(nextValue?.teamId || '')}
                  renderInput={(params) => <TextField {...params} label="Drużyna" size="small" />}
                />
              </Grid>
            </Grid>

            {selectedTeam ? (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
                  <SportsSoccerIcon color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {selectedTeam.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      team_id: {selectedTeam.teamId} | Elo: {selectedTeam.eloRanking ?? 'brak'}
                    </Typography>
                  </Box>
                  <Chip label={`dom ${filledHome}/${matchesPerSide}`} color={filledHome === matchesPerSide ? 'success' : 'default'} />
                  <Chip label={`wyjazd ${filledAway}/${matchesPerSide}`} color={filledAway === matchesPerSide ? 'success' : 'default'} />
                </Stack>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Mecze u siebie
                    </Typography>
                    <Stack spacing={1.5}>
                      {homeSlots.map((slot, index) => renderSlot('home', index, slot))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Mecze na wyjeździe
                    </Typography>
                    <Stack spacing={1.5}>
                      {awaySlots.map((slot, index) => renderSlot('away', index, slot))}
                    </Stack>
                  </Grid>
                </Grid>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <AddCircleOutlineIcon />}
                    onClick={save}
                    disabled={!canSave}
                  >
                    Dodaj mecze
                  </Button>
                </Stack>
              </>
            ) : (
              <Typography color="text.secondary">Brak drużyn w wybranych rozgrywkach.</Typography>
            )}
          </Paper>

        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, bgcolor: '#1e1e1e' }}>
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Postęp
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Mecze: {data.matchCount}/{data.expectedMatchCount} | drużyny kompletne: {data.completeTeamCount}/{data.teamCount}
              </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Drużyna</TableCell>
                  <TableCell align="center">D</TableCell>
                  <TableCell align="center">W</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.teams.map((team) => (
                  <TableRow
                    hover
                    key={team.teamId}
                    selected={team.teamId === selectedTeamId}
                    onClick={() => setSelectedTeamId(team.teamId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {team.complete && <CheckCircleIcon color="success" fontSize="small" />}
                        <Typography variant="body2" sx={{ fontWeight: team.teamId === selectedTeamId ? 800 : 400 }}>
                          {team.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={`${team.homeCount}/${matchesPerSide}`} color={team.homeCount === matchesPerSide ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={`${team.awayCount}/${matchesPerSide}`} color={team.awayCount === matchesPerSide ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 2, p: 2, bgcolor: '#1e1e1e', overflow: 'auto' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Dodane mecze
        </Typography>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Gospodarz</TableCell>
              <TableCell>Gość</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Elo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.matches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Nie dodano jeszcze żadnego meczu.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.matches.map((match) => (
              <TableRow key={match.matchId} hover>
                <TableCell>{match.matchId}</TableCell>
                <TableCell>{match.homeTeamName}</TableCell>
                <TableCell>{match.awayTeamName}</TableCell>
                <TableCell>{match.status}</TableCell>
                <TableCell align="right">{match.homeElo ?? '-'} : {match.awayElo ?? '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default EuropeDrawsManager;
